import { useState, useEffect, useRef, useMemo } from 'react';
import { CarData } from '../components/CarInfo/CarInfo';
import { useConsecutiveEvents } from './useConsecutiveEvents';
import { getConfig } from '../constants';

interface UseCarSortationDemoReturn {
  currentCar: CarData | null;
  isConnected: boolean;
  error: string | null;
}

const DEMO_CARS: CarData[] = [
  {
    ble_identifier: 'demo_car_001',
    vin: 'DEMO_VIN_001',
    color: 'Red',
    model: 'Jetta',
    year: '2024',
    designation: 'Lora OBD detected',
  },
  {
    ble_identifier: 'demo_car_002',
    vin: 'DEMO_VIN_002',
    color: 'Blue',
    model: 'Golf',
    year: '2023',
    designation: 'Lora OBD detected',
  },
  {
    ble_identifier: 'demo_car_003',
    vin: 'DEMO_VIN_003',
    color: 'White',
    model: 'Passat',
    year: '2024',
    designation: 'Lora OBD detected',
  },
  {
    ble_identifier: 'demo_ready',
    vin: 'SYSTEM',
    color: 'System',
    model: 'System',
    year: '2024',
    designation: 'READY',
  },
];

export const useCarSortationDemo = (): UseCarSortationDemoReturn => {
  const [currentCar, setCurrentCar] = useState<CarData | null>(null);
  const [isConnected] = useState(true); // Always connected in demo mode
  const [error] = useState<string | null>(null); // No errors in demo mode
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const threshold = useMemo(() => {
    return getConfig().CONSECUTIVE_EVENTS_THRESHOLD;
  }, []);

  const { processEvent } = useConsecutiveEvents({
    threshold,
  });

  useEffect(() => {
    console.log('Demo mode: Starting client-side car simulation');

    const readyState = DEMO_CARS[3];

    processEvent(readyState, setCurrentCar);
    processEvent(readyState, setCurrentCar);

    const startSimulation = () => {
      const simulateCarDetection = () => {
        const randomCar =
          DEMO_CARS[Math.floor(Math.random() * DEMO_CARS.length)];

        console.log(
          `Simulating car detection: ${randomCar.vin} - ${randomCar.designation}`,
          randomCar,
        );

        // If it's READY state, process it twice since READY requires 2 consecutive events
        if (randomCar.designation === 'READY') {
          processEvent({ ...randomCar }, setCurrentCar);
          processEvent({ ...randomCar }, setCurrentCar);
        } else {
          processEvent({ ...randomCar }, setCurrentCar);
        }
      };

      const scheduleNextCar = () => {
        const randomDelay = Math.random() * 4000 + 1000;
        intervalRef.current = setTimeout(() => {
          simulateCarDetection();
          scheduleNextCar();
        }, randomDelay);
      };

      setTimeout(() => {
        simulateCarDetection();
        scheduleNextCar();
      }, 100);
    };

    setTimeout(startSimulation, 2000);

    return () => {
      if (intervalRef.current) {
        clearTimeout(intervalRef.current);
      }
    };
  }, [processEvent]);

  return {
    currentCar,
    isConnected,
    error,
  };
};
